import { authFetch } from '../utils/authFetch';
// Enrich Layer API — calls via Netlify Function proxy

const BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions/enrich'
  : '/.netlify/functions/enrich';

export async function enrichPerson(linkedinUrl) {
  const res = await authFetch(`${BASE}?type=person&url=${encodeURIComponent(linkedinUrl)}`);
  if (res.status === 404) throw new Error('Profilen hittades inte på LinkedIn');
  if (!res.ok) throw new Error(`Enrichment-fel: ${res.status}`);
  return res.json();
}

export async function enrichCompany(linkedinUrl) {
  const res = await authFetch(`${BASE}?type=company&url=${encodeURIComponent(linkedinUrl)}`);
  if (res.status === 404) throw new Error('Företaget hittades inte på LinkedIn');
  if (!res.ok) throw new Error(`Enrichment-fel: ${res.status}`);
  return res.json();
}

// Hämtar nuvarande medarbetare (enrich_profiles=enrich ger full profil per person)
export async function enrichCompanyEmployees(linkedinUrl, page = null) {
  const params = new URLSearchParams({ type: 'employees', url: linkedinUrl });
  if (page) params.set('page', page);
  const res = await authFetch(`${BASE}?${params}`);
  if (res.status === 404) throw new Error('Företaget hittades inte på LinkedIn');
  if (!res.ok) throw new Error(`Enrichment-fel: ${res.status}`);
  return res.json(); // { results: [{profile_url, profile}], next_page }
}

// ── Översättningar ────────────────────────────────────────────────────────────

const COMPANY_TYPE_SV = {
  'Educational Institution':  'Utbildningsinstitution',
  'Government Agency':        'Myndighet',
  'Non Profit':               'Ideell organisation',
  'Nonprofit':                'Ideell organisation',
  'Partnership':              'Handelsbolag',
  'Privately Held':           'Privat aktiebolag',
  'Public Company':           'Publikt aktiebolag',
  'Self Employed':            'Egenföretagare',
  'Sole Proprietorship':      'Enskild firma',
};

const COUNTRY_SV = {
  'Sweden':          'Sverige',
  'Norway':          'Norge',
  'Denmark':         'Danmark',
  'Finland':         'Finland',
  'Germany':         'Tyskland',
  'United Kingdom':  'Storbritannien',
  'United States':   'USA',
  'Netherlands':     'Nederländerna',
  'France':          'Frankrike',
  'Spain':           'Spanien',
  'Italy':           'Italien',
  'Poland':          'Polen',
  'Switzerland':     'Schweiz',
  'Austria':         'Österrike',
  'Belgium':         'Belgien',
  'Portugal':        'Portugal',
};

const LANG_PROFICIENCY_SV = {
  'NATIVE_OR_BILINGUAL':   'Modersmål',
  'FULL_PROFESSIONAL':     'Professionell',
  'PROFESSIONAL_WORKING':  'Arbetsförmåga',
  'LIMITED_WORKING':       'Grundläggande',
  'ELEMENTARY':            'Nybörjare',
};

function sv(map, key) {
  if (!key) return '';
  return map[key] || key;
}

// ── Mappning: person → kontaktfält ────────────────────────────────────────────

export function mapPersonToContact(data) {
  if (!data) return {};
  const exp  = data.experiences?.[0] || {};
  const edu  = data.education?.[0]   || {};

  // Plats: city + country på svenska
  const city    = data.city    || exp.location || '';
  const country = sv(COUNTRY_SV, data.country || '');
  const location = [city, country].filter(Boolean).join(', ');

  // Utbildning: senaste examen + skola
  const degree = [edu.degree_name, edu.field_of_study].filter(Boolean).join(' – ');
  const education = degree && edu.school ? `${degree} (${edu.school})` : (edu.school || degree || '');

  // Språk med nivå på svenska
  const languages = (data.languages || [])
    .map(l => {
      const lvl = sv(LANG_PROFICIENCY_SV, l.proficiency || '');
      return lvl ? `${l.name} (${lvl})` : l.name;
    })
    .join(', ');

  // Nuvarande arbetsgivare och rolltitel
  const currentCompany = exp.company || '';
  const currentRole    = exp.title   || data.occupation || data.headline || '';

  // LinkedIn-rubrik (headline)
  const linkedinHeadline = data.headline || '';

  // E-post och telefon — EnrichLayer returnerar dessa bara för betalda/connected profiler
  const email = data.personal_emails?.[0] || '';
  const phone = data.personal_numbers?.[0] || '';

  // Profilbild
  const photoUrl = data.profile_pic_url || '';


  // Anställningshistorik (senaste 5)
  const experience = (data.experiences || []).slice(0, 5).map(e => {
    const from = e.starts_at ? `${e.starts_at.month ? String(e.starts_at.month).padStart(2,'0') + '/' : ''}${e.starts_at.year}` : '';
    const to   = e.ends_at   ? `${e.ends_at.month   ? String(e.ends_at.month).padStart(2,'0')   + '/' : ''}${e.ends_at.year}`   : 'nu';
    const period = from ? `${from}–${to}` : '';
    return [e.title, e.company, period].filter(Boolean).join(' · ');
  }).join('\n');

  // Certifikat
  const certifications = (data.certifications || [])
    .map(c => [c.name, c.authority].filter(Boolean).join(' – '))
    .join(', ');

  // Skills/kompetenser
  const skills = (data.skills || []).map(s => s.name || s).filter(Boolean).join(', ');

  return {
    name:              data.full_name                || '',
    role:              currentRole,
    email,
    phone,
    photoUrl,
    location,
    summary:           linkedinHeadline,
    linkedinHeadline,
    currentCompany,
    education,
    languages,
    experience,
    certifications,
    skills,
  };
}

// ── Mappning: företag → formulärfält ──────────────────────────────────────────

export function mapCompanyToForm(data) {
  const hq = data.hq || {};
  const size = Array.isArray(data.company_size)
    ? data.company_size.filter(Boolean).join('–')
    : data.company_size || '';

  // city på svenska om möjligt
  const city = hq.city || '';
  const country = sv(COUNTRY_SV, hq.country || '');
  const fullCity = city && country && country !== city ? `${city}, ${country}` : (city || country || '');

  return {
    name:        data.name        || '',
    website:     data.website     || '',
    description: data.description || '',
    employees:   size,
    foundedYear: data.founded_year ? String(data.founded_year) : '',
    companyType: sv(COMPANY_TYPE_SV, data.company_type || ''),
    city:        fullCity,
    address:     [hq.line1, hq.line2].filter(Boolean).join(', '),
    specialties: Array.isArray(data.specialities) ? data.specialities.join(', ') : '',
    logoUrl:     data.profile_pic_url || '',
  };
}
